import Modal from '~/components/Modal';
import usePublicPostApi from '~/public/lib/usePublicPostApi';
import * as ClientApi from '~/lib/ClientApi';
import { useContext, useEffect, useState } from 'react';
import TextInput from '~/components/TextInput';
import GoogleIcon from '~/components/icons/GoogleIcon';
import AppleIcon from '~/components/icons/AppleIcon';
import * as Toast from '~/lib/Toast';
import Loading from '~/components/Loading';
import { FormContext } from '~/components/forms/FormContext.jsx';
import ThemedForm from '~/components/forms/ThemedForm.jsx';
import ErrorMessage from '~/components/ErrorMessage';
import ModalContainer from '~/components/ModalContainer.jsx';

const LoginOptions = ({
    onSendCode,
    onGoogleLogin,
    onAppleLogin,
    email,
    setEmail,
}) => {
    function doSendCode(e)
    {
        e.preventDefault();
        e.stopPropagation();
        if (email) {
            onSendCode();
        }
    }

    return (
        <div>
            <div className="text-gray-500 mb-2">
                We’ll check to see if you have an account, and help create one
                if you don’t.
            </div>
            {/*<form*/}
            {/*    onSubmit={doSendCode}*/}
            {/*>*/}
                <TextInput
                    placeholder="Email Address"
                    label="Email"
                    required={true}
                    autoFocus={true}
                    type="email"
                    value={email}
                    onChange={setEmail}
                />
                <button className="form-button w-full mt-2" type="button" onClick={doSendCode}>Continue</button>
            {/*</form>*/}
            <div className="flex w-full items-center space-x-2  my-3 justify-between">
                <div className="border-t flex-1" />
                <div className="text-gray-500 text-xs">OR</div>
                <div className="border-t flex-1" />
            </div>
            <button
                type="button"
                className="mt-2 flex items-center justify-between w-full bg-white border border-gray-300 rounded-lg p-2"
                onClick={onGoogleLogin}
            >
                <GoogleIcon /> Continue with Google
                <div></div>
            </button>
            {/*<button*/}
            {/*    type="button"*/}
            {/*    className="mt-2 flex items-center justify-between w-full bg-white border border-gray-300 rounded-lg p-2"*/}
            {/*    onClick={onAppleLogin}*/}
            {/*>*/}
            {/*    <AppleIcon /> Continue with Apple*/}
            {/*    <div></div>*/}
            {/*</button>*/}
        </div>
    );
};

const ClientLoginModal = ({ isOpen, onClose }) => {
    const { form } = useContext(FormContext);

    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [showVerifyCode, setShowVerifyCode] = useState(false);
    const [sentMessage, setSentMessage] = useState('');
    const [showCodeError, setShowCodeError] = useState(false);

    const { save: sendCode, isSaving: isSendingCode } =
        usePublicPostApi(`/account/otp/email`);
    const { save: login, isSaving: isLoggingIn } =
        usePublicPostApi(`/account/otp/verify`);

    useEffect(() => {
        if (!isOpen) setShowVerifyCode(false);
    }, [isOpen]);

    function initiateOtp() {
        setSentMessage('');
        setCode('');
        setShowVerifyCode(true);
        setShowCodeError(null);
        sendCode({
            email,
        })
            .then(result => {
                setSentMessage(result.message);
            })
            .catch(() => {
                setShowVerifyCode(false);
                Toast.error('Failed to send code');
            });
    }

    function loginWithOtp() {
        if (!code || code.length !== 6) {
            setShowCodeError('Code must be exactly 6 digits');
            return;
        }

        setShowCodeError(null);
        login(
            {
                email,
                code,
            },
            {
                onErrorMessage: () => {
                    setShowCodeError('Code is not valid, please try again');
                },
            },
        ).then(result => {
            setSentMessage(JSON.stringify(result));
            ClientApi.setToken(result.token);
        });
    }

    useEffect(() => {
        function onMessage(e) {
            try {
                const data = JSON.parse(e.data);
                if (data?.token) {
                    ClientApi.setToken(data.token);
                    e.source.postMessage('close', '*');
                }
            } catch (e) {
                return;
            }
        }
        window.addEventListener('message', onMessage);

        return () => window.removeEventListener('message', onMessage);
    }, []);

    function loginWithGoogle() {
        window.open('/client-account/google', '_blank');
    }

    function loginWithApple() {
        window.open('/client-account/apple', '_blank');
    }

    return (
        <ModalContainer isOpen={isOpen} onClose={onClose} panelClassName="mx-0">
            <ThemedForm styles={form?.settings?.styles}>
                <h2 className="mb-4">Login/Register</h2>
                {!showVerifyCode && (
                    <LoginOptions
                        onSendCode={initiateOtp}
                        onAppleLogin={loginWithApple}
                        onGoogleLogin={loginWithGoogle}
                        email={email}
                        setEmail={setEmail}
                    />
                )}
                {showVerifyCode && (
                    <div>
                        {isSendingCode && <Loading message="Sending code..." />}
                        {!isSendingCode && (
                            <>
                                <div className="text-gray-500 mb-6">
                                    Once you enter the code we sent to{' '}
                                    <span className="font-semibold">
                                        {email}
                                    </span>
                                    , you’ll be all logged in.
                                </div>
                                <TextInput
                                    label="Code"
                                    placeholder="Type code here..."
                                    value={code}
                                    onChange={setCode}
                                    autoFocus={true}
                                    inputmode="numeric"
                                    autocomplete="one-time-code"
                                />
                                {showCodeError && (
                                    <ErrorMessage>{showCodeError}</ErrorMessage>
                                )}
                                <div className="flex items-center space-x-6 mt-4">
                                    <button
                                        type="button"
                                        className="form-button"
                                        onClick={loginWithOtp}
                                    >
                                        {isLoggingIn
                                            ? 'Verifying...'
                                            : 'Continue'}
                                    </button>
                                    <button
                                        type="button"
                                        className="text-gray-500 hover:underline"
                                        onClick={() => setShowVerifyCode(false)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </ThemedForm>
        </ModalContainer>
    );
};

export default ClientLoginModal;
